/* =====================================================================
   AstaLab 2.0 — bootstrap: l'UNICO punto del codice che decide quale
   BackendAdapter usare. Nessun'altra parte dell'applicazione (pagine,
   repository, motore) importa mai SupabaseBackendAdapter o
   ArtifactDemoBackendAdapter direttamente — solo questo modulo, e solo
   qui. Questo è ciò che rende vera la regola "niente if (artifact)
   sparso nel codice": la scelta avviene UNA VOLTA, qui, in base
   esclusivamente alla configurazione di rete disponibile (mai da un
   controllo esplicito "siamo nell'Artifact?").

   getBackend() è asincrona apposta: quando è disponibile una
   configurazione di rete valida, importa SupabaseBackendAdapter con un
   `import()` dinamico, cosi' il relativo codice (e la dipendenza
   @supabase/supabase-js) non finisce MAI nel bundle Artifact, che non
   percorre mai questo ramo (window.__ASTALAB_ENV__ non viene definito
   da build/bundle-artifact.mjs — vedi config/env.js) — non solo non lo
   esegue, non lo contiene proprio come bytes pubblicati.
   ===================================================================== */
import { loadEnvConfig } from "../config/env.js";
import { ArtifactDemoBackendAdapter } from "./ArtifactDemoBackendAdapter.js";

let _cachedPromise = null;
let _cachedCatalogPromise = null;

/**
 * @returns {Promise<{adapter: import("./BackendAdapter.js").BackendAdapter, env: ReturnType<typeof loadEnvConfig>}>}
 */
export function getBackend(){
  if(_cachedPromise) return _cachedPromise;
  _cachedPromise = (async () => {
    const env = loadEnvConfig();
    if(env.hasNetworkBackend){
      const { SupabaseBackendAdapter } = await import("./SupabaseBackendAdapter.js");
      return { adapter: new SupabaseBackendAdapter(env.supabaseUrl, env.supabasePublishableKey, env.authRedirectUrl), env };
    }
    return { adapter: new ArtifactDemoBackendAdapter(), env };
  })();
  return _cachedPromise;
}

/** Solo per i test: permette di ripartire da zero fra un test e l'altro (e di forzare un
 *  adapter già costruito, es. un demo adapter con store isolato per ogni test). */
export function __resetBackendForTests(adapterOverride, envOverride){
  if(adapterOverride){
    _cachedPromise = Promise.resolve({ adapter: adapterOverride, env: envOverride || loadEnvConfig() });
  } else {
    _cachedPromise = null;
  }
}

/**
 * Stesso principio di getBackend(), per il CatalogRepository (round alpha.2, obiettivo 11):
 * SupabaseCatalogRepository viene importato dinamicamente SOLO quando esiste una configurazione
 * di rete valida, cosi' non finisce mai nel bundle Artifact (che non definisce mai
 * window.__ASTALAB_ENV__ — vedi build/bundle-artifact.mjs).
 * @returns {Promise<{repository: import("../catalog/CatalogRepository.js").CatalogRepository, env: ReturnType<typeof loadEnvConfig>}>}
 */
export function getCatalogRepository(){
  if(_cachedCatalogPromise) return _cachedCatalogPromise;
  _cachedCatalogPromise = (async () => {
    const env = loadEnvConfig();
    if(env.hasNetworkBackend){
      const { SupabaseCatalogRepository } = await import("../catalog/SupabaseCatalogRepository.js");
      return { repository: new SupabaseCatalogRepository(env.supabaseUrl, env.supabasePublishableKey), env };
    }
    const { ArtifactDemoCatalogRepository } = await import("../catalog/ArtifactDemoCatalogRepository.js");
    return { repository: new ArtifactDemoCatalogRepository(), env };
  })();
  return _cachedCatalogPromise;
}

/** Solo per i test: come __resetBackendForTests, per il CatalogRepository. */
export function __resetCatalogRepositoryForTests(repositoryOverride, envOverride){
  if(repositoryOverride){
    _cachedCatalogPromise = Promise.resolve({ repository: repositoryOverride, env: envOverride || loadEnvConfig() });
  } else {
    _cachedCatalogPromise = null;
  }
}
